import React, { useEffect, useMemo, useRef, useState } from "react";
import { Wrapper } from "./style";
import { Helmet } from "react-helmet";
import { Button, ConfigProvider, message, Table, Tag, Pagination, Spin } from "antd";
import {
  CheckCircleFilled,
  LeftOutlined,
  RightOutlined,
  DownloadOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { MakeApiCall } from "../../../apis";

const Billing = (props) => {
  const { userData } = props;

  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const plansScrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [billingData, setBillingData] = useState(null);

  // Plans fetched dynamically
  const [availablePlans, setAvailablePlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);

  // Payment History (Invoices) states
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceList, setInvoiceList] = useState([]);
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoicePerPage, setInvoicePerPage] = useState(20);
  const [invoiceTotal, setInvoiceTotal] = useState(0);

  // Payment Methods states
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [billingInfo, setBillingInfo] = useState({
    workspace: "Sellerview",
    billing_email: "billing@sellerview.ai",
    currency: "USD"
  });

  // Fetch dynamic plans from API
  const getAvailablePlans = async () => {
    setPlansLoading(true);
    try {
      const response = await MakeApiCall(
        `billing/plans`,
        "get",
        null,
        true,
        {},
        userData?.token
      );
      if (response?.status && Array.isArray(response.data)) {
        // Convert data to format suitable for display/components
        const formatted = response.data.map((plan, idx) => ({
          key: plan.id,
          id: plan.id,
          name: plan.name,
          description: plan.description,
          perks: plan.features,
          priceMonthly: plan.price_monthly,
          priceYearly: plan.price_yearly,
          formattedPriceMonthly: plan.formatted_price_monthly,
          formattedPriceYearly: plan.formatted_price_yearly,
          currency: plan.currency,
          cta: "Choose " + (plan.name.includes("Plan") ? plan.name.replace(" Plan", "") : plan.name),
          highlighted: idx === 0, // highlight first just as example, or adjust as you wish
        }));
        setAvailablePlans(formatted);
      } else {
        setAvailablePlans([]);
        message.warning(response?.message || "Failed to fetch available plans");
      }
    } catch (e) {
      setAvailablePlans([]);
      message.error("Error while loading available plans");
    }
    setPlansLoading(false);
  };

  // Fetch payment history (invoices) from API
  const getPaymentHistory = async (page = 1, perPage = 20) => {
    setInvoiceLoading(true);
    try {
      const response = await MakeApiCall(
        `billing/payment-history?page=${page}&per_page=${perPage}`,
        "get",
        null,
        true,
        {},
        userData?.token
      );
      if (response?.status && response?.data?.transactions) {
        setInvoiceList(response.data.transactions);
        setInvoiceTotal(Number(response.data.pagination?.total) || 0);
      } else {
        setInvoiceList([]);
        setInvoiceTotal(0);
        message.warning(response?.message || "Failed to fetch payment history");
      }
    } catch (e) {
      setInvoiceList([]);
      setInvoiceTotal(0);
      message.error("Error while loading invoices");
    }
    setInvoiceLoading(false);
  };

  // Fetch payment methods from API
  const getPaymentMethods = async () => {
    setPaymentMethodsLoading(true);
    try {
      const response = await MakeApiCall(
        `billing/payment-methods`,
        "get",
        null,
        true,
        {},
        userData?.token
      );
      if (response?.status) {
        // Handle the response data - even if empty array
        setPaymentMethods(response.data || []);
        
        // If there's billing info in the response, update it
        if (response.billing_info) {
          setBillingInfo(prev => ({
            ...prev,
            ...response.billing_info
          }));
        }
      } else {
        setPaymentMethods([]);
        message.warning(response?.message || "Failed to fetch payment methods");
      }
    } catch (e) {
      setPaymentMethods([]);
      message.error("Error while loading payment methods");
    }
    setPaymentMethodsLoading(false);
  };

  // Set default payment method
  const setDefaultPaymentMethod = async (methodId) => {
    try {
      const response = await MakeApiCall(
        `billing/payment-methods/${methodId}/default`,
        "post",
        null,
        true,
        {},
        userData?.token
      );
      
      if (response?.status) {
        message.success("Default payment method updated successfully");
        // Refresh payment methods
        getPaymentMethods();
      } else {
        message.warning(response?.message || "Failed to update default payment method");
      }
    } catch (e) {
      message.error("Error while updating default payment method");
    }
  };

  // Remove payment method
  const removePaymentMethod = async (methodId) => {
    try {
      const response = await MakeApiCall(
        `billing/payment-methods/${methodId}`,
        "delete",
        null,
        true,
        {},
        userData?.token
      );
      
      if (response?.status) {
        message.success("Payment method removed successfully");
        // Refresh payment methods
        getPaymentMethods();
      } else {
        message.warning(response?.message || "Failed to remove payment method");
      }
    } catch (e) {
      message.error("Error while removing payment method");
    }
  };

  // Change plan: dynamic API call
  const changePlan = async (planId, billingCycle) => {
    // console.log(planId, billingCycle);
    // setPlansLoading(true);
    try {
      const response = await MakeApiCall(
        "billing/change-plan",
        "post",
        {
          plan_id: planId,
          billing_cycle: billingCycle
        },
        true,
        {},
        userData?.token
      );
      if (response?.status) {
        message.success("Plan changed successfully");
        // Refresh the current plan and billing data
        await getCurrentPlan();
        // Optionally, you may want to refetch plans
        // getAvailablePlans();
      } else {
        message.warning(response?.message || "Failed to change plan.");
      }
    } catch (e) {
      message.error("Error while changing plan");
    }
    // setPlansLoading(false);
  };

  // Replaces previous renewalDate logic to use dynamic value from billingData
  const renewalDate = useMemo(() => {
    if (billingData?.subscription?.next_due_on) {
      return dayjs.unix(billingData.subscription.next_due_on);
    }
    return null;
  }, [billingData]);

  // Set currentPlan dynamically
  const currentPlan = useMemo(() => {
    if (billingData?.subscription?.plan) {
      return {
        name: billingData.subscription.plan.name,
        description: billingData.subscription.plan.description,
        perks: billingData.subscription.plan.features,
        billing_cycle: billingData.subscription.billing_cycle,
        billing_cycle_label: billingData.subscription.billing_cycle_label,
        status: billingData.subscription.status,
        status_label: billingData.subscription.status_label,
        is_trialing: billingData.subscription.is_trialing,
        remaining_days: billingData.subscription.remaining_days,
      };
    }
    return null;
  }, [billingData]);

  // Invoice Table Columns using the response format from API
  const invoiceColumns = useMemo(
    () => [
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        render: (d) => d ? dayjs(d).format("YYYY-MM-DD") : "-",
      },
      {
        title: "Plan",
        dataIndex: "plan",
        key: "plan",
        render: (p) =>
          p ? (
            <Tag className="billing-tag">{p}</Tag>
          ) : (
            <span>-</span>
          ),
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        render: (a, row) => {
          // Try to show amount with currency if provided
          if (a === undefined || a === null) return "-";
          let currency = row?.currency || "USD";
          // If amount is in cents, you may want to adjust here
          return <span className="billing-amount">{currency} {Number(a).toFixed(2)}</span>;
        },
      },
      {
        title: "Status",
        key: "status",
        dataIndex: "status",
        render: (status) =>
          status ? (
            <Tag color={status === "paid" ? "green" : status === "pending" ? "orange" : "default"}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Tag>
          ) : (
            "-"
          ),
      },
      {
        title: "Invoice",
        key: "invoice",
        render: (_, row) => {
          // Download URL may differ in your backend API.
          // Try row.invoice_url or row.download_url or row.pdf_url.
          const invoiceUrl =
            row.invoice_url || row.download_url || row.pdf_url || row.url || null;
          return invoiceUrl ? (
            <a
              href={invoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="billing-link"
              download
            >
              <Button type="link" icon={<DownloadOutlined />} className="billing-link">
                Download
              </Button>
            </a>
          ) : (
            <span className="billing-link" style={{color:'#bbb'}}>N/A</span>
          );
        },
      },
    ],
    []
  );

  const scrollPlans = (direction) => {
    const el = plansScrollRef.current;
    if (!el) return;
    const delta = direction === "left" ? -420 : 420;
    el.scrollBy({ left: delta, behavior: "smooth" });
  };

  // Determine price string for a plan according to billingPeriod
  const getPlanPriceString = (p) => {
    if (!p) return "";
    if (billingPeriod === "monthly") {
      return `${p.formattedPriceMonthly} ${p.currency}`;
    }
    return `${p.formattedPriceYearly} ${p.currency}`;
  };

  // Dynamic API call to set billingData with real response
  const getCurrentPlan = async () => {
    setLoading(true);
    const response = await MakeApiCall(`billing/my-subscription`, "get", null, true, {}, userData?.token);

    if (response?.status && response?.data) {
      setBillingData(response.data);
    } else {
      message.warning(response?.message || "Failed to fetch billing info");
      setBillingData(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (userData?.token) {
      getCurrentPlan();
      getAvailablePlans();
      getPaymentHistory(invoicePage, invoicePerPage);
      getPaymentMethods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.token]);

  useEffect(() => {
    if (userData?.token) {
      getPaymentHistory(invoicePage, invoicePerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoicePage, invoicePerPage]);

  // UI LOADING STATES and PLAN BADGING
  const isActive = !!billingData?.has_subscription && !!billingData?.subscription?.is_active;

  return (
    <Wrapper>
      <Helmet>
        <title>Billing</title>
      </Helmet>

      <div className="billing-header ">
        <div className="billing-header__left">
          <div className="billing-title">Billing</div>
          <div className="billing-subtitle">Manage your plan, download invoices, and update payment methods.</div>
        </div>
        <div className="billing-header__right">
          <div className="billing-status">
            <CheckCircleFilled className="billing-status__icon" />
            <span className="billing-status__text">
              {billingData?.subscription?.is_trialing
                ? "Trialing"
                : isActive
                ? "Active"
                : "Inactive"}
              <span className="billing-status__dot">•</span>
              {renewalDate && (
                <>
                  {" "}
                  {billingData?.subscription?.next_due_on_label
                    ? billingData.subscription.next_due_on_label
                    : "Next renewal:"}{" "}
                  <b>{renewalDate.format("MMM D, YYYY")}</b>
                </>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Plans */}
      <div className="row gy-5 g-xl-5">
        <div className="col-xxl-12">
          <div className="card card-xxl-stretch mb-5 mb-xl-8 billing-card">
            <div className="card-header border-bottom-dashed billing-card__header">
              <div>
                <div className="billing-section-title">Plans</div>
                <div className="billing-section-subtitle">Upgrade or downgrade anytime.</div>
              </div>
              <div className="billing-period">
                <div className="billing-period__label">Billing:</div>
                <div className="billing-period__toggle">
                  <button
                    className={`billing-pill ${billingPeriod === "monthly" ? "is-active" : ""}`}
                    onClick={() => setBillingPeriod("monthly")}
                    type="button"
                  >
                    Monthly
                  </button>
                  <button
                    className={`billing-pill ${billingPeriod === "yearly" ? "is-active" : ""}`}
                    onClick={() => setBillingPeriod("yearly")}
                    type="button"
                  >
                    Yearly
                  </button>
                </div>
              </div>
            </div>

            <div className="card-body pt-6">
              <div className="billing-plans-grid row gy-5">
                <div className="col-lg-3">
                  <div className="billing-current">
                    <div className="billing-current__badge">
                      {currentPlan?.is_trialing
                        ? "TRIALING"
                        : isActive
                        ? "CURRENT PLAN"
                        : "No Active Plan"}
                    </div>
                    <div className="billing-current__name">
                      {currentPlan ? currentPlan.name : "N/A"}
                    </div>
                    {/* Price and billing cycle label */}
                    <div className="billing-current__price">
                      {currentPlan && (
                        <>
                          {currentPlan.billing_cycle_label && (
                            <span>
                              {currentPlan.billing_cycle_label}
                              {currentPlan.remaining_days !== undefined &&
                                currentPlan.is_trialing && (
                                  <span>
                                    {" "}
                                    · {currentPlan.remaining_days} days left
                                  </span>
                                )}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <div className="billing-current__desc">
                      {currentPlan ? currentPlan.description : ""}
                    </div>
                    {currentPlan?.perks && (
                      <ul className="billing-plan__perks">
                        {currentPlan.perks.map((perk) => (
                          <li key={perk}>
                            <span className="billing-check" />
                            {perk}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* <div className="billing-current__actions">
                      <Button className="billing-btn" type="primary" danger>
                        Upgrade
                      </Button>
                      <Button className="billing-btn" danger ghost>
                        Downgrade
                      </Button>
                    </div> */}

                    <div className="billing-hint">
                      You can switch plans anytime. If you downgrade, features update based on your new plan.
                    </div>
                  </div>
                </div>
                <div className="col-lg-9">
                  <div className="billing-available">
                    <div className="billing-available__top">
                      <div className="billing-available__title">Available Plans</div>
                      <div className="billing-available__nav">
                        <Button
                          className="billing-icon-btn"
                          icon={<LeftOutlined />}
                          onClick={() => scrollPlans("left")}
                          disabled={plansLoading}
                        />
                        <Button
                          className="billing-icon-btn"
                          icon={<RightOutlined />}
                          onClick={() => scrollPlans("right")}
                          disabled={plansLoading}
                        />
                      </div>
                    </div>

                    <div className="billing-scroll mt-5 pb-5" ref={plansScrollRef}>
                      {plansLoading ? (
                        <div className="p-4">Loading plans...</div>
                      ) : availablePlans.length === 0 ? (
                        <div className="p-4">No plans found.</div>
                      ) : (
                        availablePlans.map((p) => (
                          <div
                            key={p.key}
                            className={`billing-plan ${p.highlighted ? "is-highlighted" : ""}`}
                          >
                            <div className="billing-plan__name mb-4">{p.name}</div>
                            <div className="billing-plan__price">
                              <span className="billing-plan__priceValue">
                                {getPlanPriceString(p)}
                              </span>
                              <span className="billing-plan__priceUnit">
                                / {billingPeriod === "monthly" ? "month" : "year"}
                              </span>
                            </div>
                            <div className="billing-current__desc">
                              {p ? p.description : ""}
                            </div>
                            <ul className="billing-plan__perks">
                              {p.perks.map((perk) => (
                                <li key={perk}>
                                  <span className="billing-check" />
                                  {perk}
                                </li>
                              ))}
                            </ul>
                            <Button
                              className="billing-plan__cta"
                              type={p.highlighted ? "primary" : "default"}
                              danger
                              onClick={() => changePlan(p.id, billingPeriod)}
                              // loading={plansLoading}
                              disabled={loading}
                            >
                              {p.cta}
                            </Button>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="billing-scrollbar-hint" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoices */}
      <div className="row gy-5 g-xl-5">
        <div className="col-xxl-12">
          <div className="card card-xxl-stretch mb-5 mb-xl-8 billing-card">
            <div className="card-header border-bottom-dashed billing-card__header">
              <div>
                <div className="billing-section-title">Invoices</div>
                <div className="billing-section-subtitle">Download previous invoices for your records.</div>
              </div>
            </div>
            <div className="card-body pt-2">
              <ConfigProvider theme={{ token: { colorPrimary: "#f45b68" } }}>
                <div className="table-responsive">
                  <Spin spinning={invoiceLoading}>
                    <Table
                      columns={invoiceColumns}
                      dataSource={invoiceList}
                      rowKey={(row) => row.id || row.transaction_id || row.invoice_id}
                      pagination={false}
                      locale={{
                        emptyText: invoiceLoading
                          ? "Loading..."
                          : "No invoices found."
                      }}
                      components={{
                        table: (p) => (
                          <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...p} />
                        ),
                        header: { row: (p) => <tr {...p} className="fw-bolder text-dark bg-cus" /> },
                      }}
                    />
                  </Spin>
                  <div className="d-flex justify-content-end mt-4">
                    <Pagination
                      current={invoicePage}
                      pageSize={invoicePerPage}
                      total={invoiceTotal}
                      onChange={(page, pageSize) => {
                        setInvoicePage(page);
                        setInvoicePerPage(pageSize);
                      }}
                      showSizeChanger
                      pageSizeOptions={["10", "20", "50", "100"]}
                      size="small"
                    />
                  </div>
                </div>
              </ConfigProvider>
            </div>
          </div>
        </div>
      </div>

      {/* Payment methods */}
      <div className="row gy-5 g-xl-5">
        <div className="col-xxl-12">
          <div className="card card-xxl-stretch mb-5 mb-xl-8 billing-card">
            <div className="card-header border-bottom-dashed billing-card__header">
              <div>
                <div className="billing-section-title">Payment methods</div>
                <div className="billing-section-subtitle">Manage cards used for workspace billing.</div>
              </div>
              <Button className="billing-btn" icon={<PlusOutlined />}>
                Add payment method
              </Button>
            </div>

            <div className="card-body pt-6">
              <Spin spinning={paymentMethodsLoading}>
                <div className="billing-payments-grid">
                  <div className="billing-payments">
                    {paymentMethods.length === 0 ? (
                      <div className="billing-no-payment-methods">
                        No payment methods found. Add one to get started.
                      </div>
                    ) : (
                      paymentMethods.map((pm) => (
                        <div key={pm.id} className={`mb-4 billing-payCard ${pm.is_default ? "is-default" : ""}`}>
                          <div className="billing-payCard__left">
                            <div className="billing-payCard__brand">{pm.brand}</div>
                            <div className="billing-payCard__meta">
                              <div className="billing-payCard__number">
                                {pm.brand} ending •••• {pm.last4}
                                {pm.is_default && <span className="billing-defaultTag">Default</span>}
                              </div>
                              <div className="billing-payCard__name">Name on card: {pm.name_on_card || pm.name}</div>
                              {pm.expiry_month && pm.expiry_year && (
                                <div className="billing-payCard__expiry">
                                  Expires: {pm.expiry_month}/{pm.expiry_year}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="billing-payCard__actions">
                            <Button 
                              className="billing-btn" 
                              disabled={pm.is_default}
                              onClick={() => setDefaultPaymentMethod(pm.id)}
                            >
                              Set default
                            </Button>
                            <Button 
                              className="billing-btn" 
                              danger 
                              ghost
                              onClick={() => removePaymentMethod(pm.id)}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="billing-billingInfo">
                    <div className="billing-billingInfo__title">Billing info</div>
                    <div className="billing-billingInfo__row">
                      <div className="billing-billingInfo__label">Workspace:</div>
                      <div className="billing-billingInfo__value">{billingInfo.workspace}</div>
                    </div>
                    <div className="billing-billingInfo__row">
                      <div className="billing-billingInfo__label">Billing email:</div>
                      <div className="billing-billingInfo__value">{billingInfo.billing_email}</div>
                    </div>
                    <div className="billing-billingInfo__row">
                      <div className="billing-billingInfo__label">Currency:</div>
                      <div className="billing-billingInfo__value">{billingInfo.currency}</div>
                    </div>
                    <Button className="billing-btn w-100" danger ghost>
                      Update billing info
                    </Button>
                    <div className="billing-hint mt-3">Keep billing email updated to receive invoices and payment alerts.</div>
                  </div>
                </div>
              </Spin>
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default Billing;